/**
 * useLivekitRoom Hook
 *
 * Manages LiveKit token fetching and connection lifecycle.
 * Returns token + serverUrl to pass to <LiveKitRoom> component.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchLivekitToken } from '../lib/livekit'

interface UseLivekitRoomOptions {
	roomName: string
	role: 'broadcaster' | 'viewer'
	participantName?: string
	enabled?: boolean
}

interface UseLivekitRoomReturn {
	token: string | null
	serverUrl: string | null
	identity: string | null
	isLoading: boolean
	error: string | null
	retry: () => void
}

export function useLivekitRoom({
	roomName,
	role,
	participantName,
	enabled = true,
}: UseLivekitRoomOptions): UseLivekitRoomReturn {
	const [token, setToken] = useState<string | null>(null)
	const [serverUrl, setServerUrl] = useState<string | null>(null)
	const [identity, setIdentity] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const retryCountRef = useRef(0)

	const fetchToken = useCallback(async () => {
		if (!roomName || !enabled) return

		setIsLoading(true)
		setError(null)

		try {
			const result = await fetchLivekitToken(roomName, role, participantName)
			setToken(result.token)
			setServerUrl(result.serverUrl)
			setIdentity(result.identity)
			retryCountRef.current = 0
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to fetch token'
			setError(message)
			setToken(null)
			setServerUrl(null)
			setIdentity(null)
		} finally {
			setIsLoading(false)
		}
	}, [roomName, role, participantName, enabled])

	useEffect(() => {
		fetchToken()
	}, [fetchToken])

	const retry = useCallback(() => {
		retryCountRef.current += 1
		fetchToken()
	}, [fetchToken])

	return { token, serverUrl, identity, isLoading, error, retry }
}
